import React from 'react';
import Link from 'next/link';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { sanitizeHTML } from '@loomstateorg/shared-frontend/helpers/domSanitizer';

interface SanityRichTextProps {
  content: any[]; // Sanity Portable Text array
  className?: string;
  tableHeadColor?: string;
  tableHeadTextColor?: string;
  tableBodyColor?: string;
  tableBodyTextColor?: string;
}

const SanityRichText: React.FC<SanityRichTextProps> = ({
  content,
  className = '',
  tableHeadColor,
  tableHeadTextColor,
  tableBodyColor,
  tableBodyTextColor,
}) => {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return null;
  }

  const paragraphClass = `mb-10 text-left xl:text-justify ${className}`;

  // Custom components for PortableText
  const components: PortableTextComponents = {
    // Block types
    block: {
      h1: ({ children }: { children?: React.ReactNode }) => <h1 className="mb-24 text-4xl font-bold">{children}</h1>,
      h2: ({ children }: { children?: React.ReactNode }) => <h2 className="mb-20 text-3xl font-bold">{children}</h2>,
      h3: ({ children }: { children?: React.ReactNode }) => <h3 className="mb-16 text-2xl font-bold">{children}</h3>,
      h4: ({ children }: { children?: React.ReactNode }) => <h4 className="mb-14 text-xl font-bold">{children}</h4>,
      h5: ({ children }: { children?: React.ReactNode }) => <h5 className="mb-12 text-lg font-bold">{children}</h5>,
      h6: ({ children }: { children?: React.ReactNode }) => <h6 className="mb-10 text-base font-bold">{children}</h6>,
      normal: ({ children }: { children?: React.ReactNode }) => (
        <p className={paragraphClass}>{children}</p>
      ),
      blockquote: ({ children }: { children?: React.ReactNode }) => (
        <blockquote className="mb-24 border-l-4 border-gray-300 pl-16 italic">{children}</blockquote>
      ),
    },
    // List types
    list: {
      bullet: ({ children }: { children?: React.ReactNode }) => <ul className="mb-24 list-disc pl-24">{children}</ul>,
      number: ({ children }: { children?: React.ReactNode }) => (
        <ol className="mb-24 list-decimal pl-24">{children}</ol>
      ),
    },
    listItem: {
      bullet: ({ children }: { children?: React.ReactNode }) => <li className="mb-8">{children}</li>,
      number: ({ children }: { children?: React.ReactNode }) => <li className="mb-8">{children}</li>,
    },
    // Marks (inline formatting)
    marks: {
      strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-body">{children}</strong>,
      em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
      underline: ({ children }: { children?: React.ReactNode }) => <u>{children}</u>,
      code: ({ children }: { children?: React.ReactNode }) => <code>{children}</code>,
      link: ({ value, children }: { value?: { href?: string }; children?: React.ReactNode }) => {
        const href = value?.href || '';
        const isExternal = href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');

        if (isExternal) {
          return (
            // testid-ignore: CMS inline link; tests find it by text inside the rich-text container
            <a
              href={href}
              className="text-primary hover:text-primary/80 underline underline-offset-4"
              target="_blank"
              rel="noreferrer"
            >
              {children}
            </a>
          );
        }

        return (
          // testid-ignore: CMS inline link; tests find it by text inside the rich-text container
          <Link href={href} className="text-primary hover:text-primary/80 underline underline-offset-4">
            {children}
          </Link>
        );
      },
    },
    // Table support - handles Sanity table blocks
    types: {
      htmlBlock: ({ value }: { value?: any }) => {
        const raw =
          typeof value === 'string'
            ? value
            : value?.html || value?.code || value?.content || value?.body || '';
        if (!raw?.trim()) return null;
        return (
          <div
            className="rich-text-content w-full"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(raw) }}
          />
        );
      },
      styledTable: ({ value }: { value?: any }) => {
        if (!value) return null;

        const tableStyle = value.tableStyle || 'table-style-one';
        const tableData = value.table;
        const rows = tableData?.rows || [];
        if (!Array.isArray(rows) || rows.length === 0) return null;

        const getCellText = (cell: any): string => {
          if (typeof cell === 'string') return cell;
          if (cell.text) return cell.text;
          if (cell.value) return cell.value;
          return '';
        };

        const renderCellContent = (cell: any) => {
          if (cell.content && Array.isArray(cell.content)) {
            return <PortableText value={cell.content} components={components} />;
          }
          if (cell.children && Array.isArray(cell.children)) {
            return <PortableText value={cell.children} components={components} />;
          }
          return getCellText(cell);
        };

        const headerRow = rows[0];
        const headerCells = headerRow?.cells || headerRow || [];
        const bodyRows = rows.length > 1 ? rows.slice(1) : [];

        if (tableStyle === 'table-style-two') {
          const title = getCellText(headerCells[0]);

          const defTitleStyle: React.CSSProperties = {};
          if (tableHeadColor) defTitleStyle.backgroundColor = tableHeadColor;
          if (tableHeadTextColor) defTitleStyle.color = tableHeadTextColor;

          const defBodyStyle: React.CSSProperties = {};
          if (tableBodyColor) defBodyStyle.backgroundColor = tableBodyColor;
          if (tableBodyTextColor) defBodyStyle.color = tableBodyTextColor;

          return (
            <div className="mb-24 w-full min-w-0 overflow-x-auto scrollbar-hide">
              <table className="w-full table-fixed border-collapse">
                <colgroup>
                  <col style={{ width: '180px' }} />
                  <col />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th
                      colSpan={2}
                      className="text-left text-sm font-primaryBold leading-[48px]"
                      style={Object.keys(defTitleStyle).length > 0 ? defTitleStyle : undefined}
                    >
                      {title}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bodyRows.map((row: any, rowIndex: number) => {
                    const cells = row.cells || row || [];
                    const label = getCellText(cells[0]);
                    const description = getCellText(cells[1]);
                    return (
                      <tr
                        key={row._key || `def-${rowIndex}`}
                        style={Object.keys(defBodyStyle).length > 0 ? defBodyStyle : undefined}
                      >
                        <td
                          className="align-middle py-12 pr-10 font-primaryBold text-table-label leading-snug"
                          style={tableBodyTextColor ? { color: tableBodyTextColor } : undefined}
                        >
                          {label}
                        </td>
                        <td className="align-middle py-12 pl-5 text-left font-primaryThin leading-snug">
                          {description}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }

        const headerCellStyle: React.CSSProperties = {};
        if (tableHeadColor) headerCellStyle.backgroundColor = tableHeadColor;
        if (tableHeadTextColor) headerCellStyle.color = tableHeadTextColor;

        const bodyRowStyle: React.CSSProperties = {};
        if (tableBodyColor) bodyRowStyle.backgroundColor = tableBodyColor;
        if (tableBodyTextColor) bodyRowStyle.color = tableBodyTextColor;

        const sizeColumnCount = Math.max(headerCells.length - 1, 1);
        const sizeHeaders = headerCells.slice(1);
        const measureHeader = getCellText(headerCells[0]);

        return (
          <div className="mb-24 w-full min-w-0">
            {/* Mobile: measurement blocks with sizes stacked vertically */}
            <div className="md:hidden">
              <div
                className="border-b border-black py-8 text-sm font-primaryBold"
                style={Object.keys(headerCellStyle).length > 0 ? headerCellStyle : undefined}
              >
                {measureHeader}
              </div>
              <div className="divide-y divide-gray-200">
                {bodyRows.map((row: any, rowIndex: number) => {
                  const cells = row.cells || row || [];
                  const measureLabel = getCellText(cells[0]);
                  return (
                    <div
                      key={row._key || row.id || `mobile-measure-${rowIndex}`}
                      className="py-12"
                      style={Object.keys(bodyRowStyle).length > 0 ? bodyRowStyle : undefined}
                    >
                      <p
                        className="mb-10 font-primaryBold leading-snug text-secondary-regular"
                        style={tableBodyTextColor ? { color: tableBodyTextColor } : undefined}
                      >
                        {measureLabel || renderCellContent(cells[0])}
                      </p>
                      <dl>
                        {sizeHeaders.map((sizeCell: any, sizeIndex: number) => {
                          const sizeLabel = getCellText(sizeCell);
                          const valueCell = cells[sizeIndex + 1];
                          const sizeValue = getCellText(valueCell);
                          return (
                            <div
                              key={sizeCell._key || sizeCell.id || `mobile-size-${rowIndex}-${sizeIndex}`}
                              className="grid grid-cols-[64px_1fr] items-center gap-x-24 border-b border-gray-200 py-10 last:border-b-0"
                            >
                              <dt className="text-sm font-primaryBold">{sizeLabel}</dt>
                              <dd className="pl-8 text-left font-primaryThin leading-snug">
                                {sizeValue || renderCellContent(valueCell)}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop: horizontal size chart table */}
            <table className="hidden w-full table-fixed border-collapse md:table">
              <colgroup>
                <col style={{ width: '38%' }} />
                {Array.from({ length: sizeColumnCount }).map((_, colIndex) => (
                  <col key={`size-col-${colIndex}`} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-black">
                  {headerCells.map((cell: any, cellIndex: number) => {
                    const text = getCellText(cell);
                    const isFirstCol = cellIndex === 0;
                    return (
                      <th
                        key={cell._key || cell.id || `header-cell-${cellIndex}`}
                        className={`text-sm font-primaryBold ${
                          isFirstCol
                            ? 'break-words py-8 pr-8 text-left align-middle leading-snug whitespace-normal'
                            : 'px-4 py-8 text-center align-middle whitespace-nowrap leading-snug'
                        }`}
                        style={Object.keys(headerCellStyle).length > 0 ? headerCellStyle : undefined}
                      >
                        {text || renderCellContent(cell)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bodyRows.map((row: any, rowIndex: number) => {
                  const cells = row.cells || row || [];
                  return (
                    <tr key={row._key || row.id || `body-${rowIndex}`} style={bodyRowStyle}>
                      {cells.map((cell: any, cellIndex: number) => {
                        const text = getCellText(cell);
                        const isFirstCol = cellIndex === 0;
                        return (
                          <td
                            key={cell._key || cell.id || `body-cell-${cellIndex}`}
                            className={`font-primaryThin ${
                              isFirstCol
                                ? 'break-words py-8 pr-8 text-left align-middle leading-snug whitespace-normal'
                                : 'px-2 py-8 text-center align-middle whitespace-nowrap leading-snug'
                            }`}
                          >
                            {text || renderCellContent(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      },
    },
  };

  // Add fallback for unknown types that might be tables
  const componentsWithFallback: PortableTextComponents = {
    ...components,
    types: {
      ...components.types,
      // Fallback handler for unknown types
      unknownType: ({ value, isInline }: { value?: any; isInline?: boolean }) => {
        if (value?._type === 'styledTable' || value?.tableStyle) {
          const styledTableComponent = components.types?.styledTable as ((props: { value?: any }) => React.ReactNode) | undefined;
          if (styledTableComponent) {
            return styledTableComponent({ value });
          }
        }
        if (value?._type && (value._type.includes('table') || value.rows)) {
          const styledTableComponent = components.types?.styledTable as ((props: { value?: any }) => React.ReactNode) | undefined;
          if (styledTableComponent) {
            return styledTableComponent({ value: { tableStyle: 'table-style-one', table: value } });
          }
        }
        return <div>Unknown type: {value?._type}</div>;
      },
    },
  };

  return (
    <div className={`w-full ${className}`} data-testid="atom-sanity-rich-text">
      <PortableText value={content} components={componentsWithFallback} />
    </div>
  );
};

export default SanityRichText;
